/*
 * What the map's corner credits, per source. OpenLayers shows a source's
 * credit only while a layer drawing it is in view, and drops duplicates.
 *
 * Names only. The radar networks' licences ask for more than a name (CC BY
 * 4.0 wants the licence named and linked and the changes indicated, Licence
 * Ouverte the source, IMGW that its data "has been processed by" whoever shows
 * it). All of that lives in imprint.html#data, which the imprint credit links
 * as "Licences" beside the base map's credit on every map. Spelled out per
 * provider on the map, it ran three lines deep on a phone.
 */
const link = (href: string, text: string) => `<a href="${href}" target="_blank" rel="noopener">${text}</a>`;

export const osmAttribution = `© ${link("https://www.openstreetmap.org/copyright", "OpenStreetMap")} contributors`;
export const dwdAttribution = `© ${link("https://www.dwd.de/", "DWD")}`;
export const meteoSwissAttribution = `© ${link("https://www.meteoswiss.admin.ch/", "MeteoSwiss")}`;
export const meteoFranceAttribution = `© ${link("https://meteofrance.com/", "Météo-France")}`;
export const chmiAttribution = `© ${link("https://www.chmi.cz/", "ČHMÚ")}`;
export const imgwAttribution = `© ${link("https://www.imgw.pl/", "IMGW-PIB")}`;
/**
 * Belgium's radars have two owners: RMI (also KMI-IRM, either name its own
 * credit) runs Jabbeke and Wideumont, VMM, the Flemish Environment Agency,
 * owns Helchteren. RMI's volumes are CC BY 4.0; Helchteren's are CC BY 4.0 by
 * EUMETNET's metadata, while VMM's own blanket terms are the Flemish Modellicentie
 * Gratis Hergebruik v1.0, so the imprint names both. One composite, one credit.
 */
export const rmiVmmAttribution = `© ${link("https://www.meteo.be/", "RMI")}, © ${link("https://vmm.vlaanderen.be/", "VMM")}`;
export const shmuAttribution = `© ${link("https://www.shmu.sk/", "SHMÚ")}`;
/** LHMT's radars are CC BY 4.0 by EUMETNET's metadata and CC BY-SA 4.0 by its own terms; the imprint names both. */
export const lhmtAttribution = `© ${link("https://www.meteo.lt/", "LHMT")}`;
/** The Estonian Environment Agency (KAUR), which asks to be named as the source, by its Estonian name. */
export const kaurAttribution = `© ${link("https://www.ilmateenistus.ee/", "Keskkonnaagentuur")}`;
/**
 * Met Éireann's terms ask for five statements wherever its data is shown;
 * the corner carries the copyright and the licence, the imprint all five.
 */
export const metEireannAttribution = `© ${link("https://www.met.ie/", "Met Éireann")} (CC BY 4.0)`;
/** DHMZ: CC BY 4.0 by EUMETNET's metadata and the Croatian Open Licence by its own; the imprint names both. */
export const dhmzAttribution = `© ${link("https://meteo.hr/", "DHMZ")}`;
export const dmiAttribution = `© ${link("https://www.dmi.dk/", "DMI")}`;
export const knmiAttribution = `© ${link("https://www.knmi.nl/", "KNMI")}`;
export const fmiAttribution = `© ${link("https://en.ilmatieteenlaitos.fi/open-data", "FMI")}`;
export const smhiAttribution = `© ${link("https://www.smhi.se/", "SMHI")}`;
/** MET Norway: NLOD 2.0 and CC BY 4.0 on its own terms, which this links; the imprint names both. */
export const metNorwayAttribution = `© ${link("https://www.met.no/en/free-meteorological-data/Licensing-and-crediting", "MET Norway")}`;
export const noaaAttribution = link("https://www.weather.gov/disclaimer", "NOAA/NWS");
export const blitzortungAttribution = `© ${link("https://www.blitzortung.org/", "Blitzortung.org")}`;
export const mapterhornAttribution = `© ${link("https://mapterhorn.com/attribution", "Mapterhorn")}`;
export const protomapsAttribution = `© ${link("https://protomaps.com", "Protomaps")}`;
export const imprintAttribution = "| <a href=\"/imprint.html\">Imprint</a> · <a href=\"/imprint.html#data\">Licences</a>";

/**
 * The order the corner reads in: the base map's credits, the data's, and the
 * imprint last, since its leading "|" is written to close the line.
 *
 * OpenLayers lists credits in the order their layers were added to the map,
 * which is no order at all here: the lightning layer is built with the page
 * and the radar networks' as their first frames land, so Blitzortung led the
 * line, the imprint came straight after it, and the networks shuffled from
 * one load to the next. A credit not listed keeps its place among the data's.
 */
export const ATTRIBUTION_ORDER = [
  osmAttribution,
  protomapsAttribution,
  mapterhornAttribution,
  dwdAttribution,
  meteoSwissAttribution,
  meteoFranceAttribution,
  chmiAttribution,
  imgwAttribution,
  rmiVmmAttribution,
  shmuAttribution,
  lhmtAttribution,
  kaurAttribution,
  metEireannAttribution,
  dhmzAttribution,
  dmiAttribution,
  knmiAttribution,
  fmiAttribution,
  smhiAttribution,
  metNorwayAttribution,
  noaaAttribution,
  blitzortungAttribution,
  imprintAttribution,
];

/** `credits` in ATTRIBUTION_ORDER; see there. */
export function orderAttributions<T>(credits: T[]): T[] {
  const last = ATTRIBUTION_ORDER.indexOf(imprintAttribution);
  const rank = (credit: T) => {
    const at = ATTRIBUTION_ORDER.indexOf(credit as unknown as string);
    return at === -1 ? last - 0.5 : at;
  };
  return credits
    .map((credit, index) => ({ credit, index, rank: rank(credit) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map(({ credit }) => credit);
}
